import React, { useState, useEffect } from 'react';
import { useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';
import './style.scss';
import { IRepository } from 'interface/repositoryInterface';
import {
  deleteRepository,
  updateRepositorySettings,
} from 'services/repositoryService';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { IReliantRepositorySettings } from 'interface/repositoryInterface';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';

interface RepositoryListProps {
  fetchRepositories: () => void;
  fetchedRepositories: IRepository[];
  repoSettings: IReliantRepositorySettings[];
}

const RepositoryList: React.FC<RepositoryListProps> = ({
  fetchRepositories,
  fetchedRepositories,
  repoSettings,
}) => {
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const [expandedRepo, setExpandedRepo] = useState<string | false>(false);
  const [isUpdated, setIsUpdated] = useState<{ [key: string]: boolean }>({});
  const [updatedSettings, setUpdatedSettings] = useState<
    IReliantRepositorySettings[]
  >(
    repoSettings.map(setting => ({
      ...setting,
      ReliantYamlPath: setting.ReliantYamlPath,
      MainBranch: setting.MainBranch,
    })),
  );

  useEffect(() => {
    if (fetchedRepositories && fetchedRepositories.length > 0) {
      setExpandedRepo(fetchedRepositories[0].ID);
    }
  }, [fetchedRepositories]);

  useEffect(() => {
    setUpdatedSettings(
      repoSettings.map(setting => ({
        ...setting,
        ReliantYamlPath: setting.ReliantYamlPath,
        MainBranch: setting.MainBranch,
      })),
    );
  }, [repoSettings]);

  const handleAccordionChange =
    (repoId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedRepo(isExpanded ? repoId : false);
    };

  const handleUpdateRepository = async (repoId: string) => {
    const repoSetting = updatedSettings.find(
      setting => setting.RepositoryID === repoId,
    );

    if (!repoSetting) {
      console.error('Repository setting not found');
      toast.error('Repository setting not found');
      return;
    }

    try {
      const response = await updateRepositorySettings({
        RepositoryID: repoSetting.RepositoryID,
        ReliantYamlPath: repoSetting.ReliantYamlPath,
        MainBranch: repoSetting.MainBranch,
        Version: repoSetting.Version,
      });
      if (response.status === 200) {
        setIsUpdated(prev => ({ ...prev, [repoId]: false }));
        toast.success('Repository updated successfully');
      }
    } catch (error) {
      console.error('Failed to update repository:', error);
      toast.error('Failed to update repository');
    }
  };

  // working do not touch
  const handleYamlPathChange = (repoId: string, newPath: string) => {
    setUpdatedSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.RepositoryID === repoId
          ? { ...setting, ReliantYamlPath: newPath }
          : setting,
      ),
    );
    setIsUpdated(prev => ({ ...prev, [repoId]: true }));
  };

  const handleMainBranchChange = (repoId: string, value: string) => {
    setUpdatedSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.RepositoryID === repoId
          ? { ...setting, MainBranch: value }
          : setting,
      ),
    );
    setIsUpdated(prev => ({ ...prev, [repoId]: true }));
  };

  const handleDeleteRepository = (repository: IRepository) => {
    deleteRepository({
      OrganizationID: organizationId,
      RepositoryID: repository.ID,
    }).then(() => {
      fetchRepositories();
    });
  };

  return (
    <div className="repo-list">
      {fetchedRepositories?.map(repo => (
        <Accordion
          key={repo.ID}
          className="selected-repo-item"
          expanded={expandedRepo === repo.ID}
          onChange={handleAccordionChange(repo.ID)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {repo.OwnerName} / {repo.Name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="repo-settings">
              <TextField
                value={
                  updatedSettings.find(
                    setting => setting.RepositoryID === repo.ID,
                  )?.MainBranch || ''
                }
                onChange={e => {
                  handleMainBranchChange(repo.ID, e.target.value);
                }}
                label="Main Branch"
                variant="outlined"
                size="small"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                placeholder="main"
              />
              <TextField
                value={
                  updatedSettings.find(
                    setting => setting.RepositoryID === repo.ID,
                  )?.ReliantYamlPath || ''
                }
                onChange={e => {
                  handleYamlPathChange(repo.ID, e.target.value);
                }}
                label="Reliant YAML Path"
                variant="outlined"
                size="small"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                placeholder="default/path"
                helperText="Path to the Reliant YAML configuration file"
              />
            </div>
          </AccordionDetails>
          <div className="repo-actions">
            {isUpdated[repo.ID] ? (
              <Button
                color="primary"
                onClick={() => handleUpdateRepository(repo.ID)}>
                Update
              </Button>
            ) : (
              <Button
                color="error"
                onClick={() => handleDeleteRepository(repo)}>
                Delete
              </Button>
            )}
          </div>
        </Accordion>
      ))}
    </div>
  );
};

export default RepositoryList;
